import type {
	FieldOption,
	JiraFieldOptionWeight,
} from '@jira/polaris__domain-field/src/FieldOption.types.tsx';
import type {
	FieldKey,
	FieldConfiguration,
} from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { createExperienceWithMetaType } from '@jira/polaris__lib-analytics/src/createJpdExperience.tsx';
import { fireCompoundAnalyticsEvent } from '@jira/polaris__lib-analytics/src/fireCompoundAnalyticsEvent.tsx';
import { getAppliedDecoration } from '@jira/polaris__component-decorations/src/getAppliedDateDecoration.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';
import { remapValueDecorations } from './performJiraConfigurationUpdate.tsx';

const fieldOptionUpdateExperience = createExperienceWithMetaType(
	experience.fieldsSidebar.fieldOptionUpdate,
	'deleteOption',
	(err) => `FieldOptionUpdate: ${err?.message ?? 'generic error'}`,
);

export const deleteOption =
	(fieldKey: FieldKey, jiraOptionId: string) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	): Promise<void> => {
		const state = getState();
		const field = state.fields[fieldKey];

		const previousOptions = field.options;
		const { valueDecorations, ...previousConfig } = field?.configuration ?? {};
		const previousOptionWeights = previousConfig.optionWeights ?? [];

		const newOptions = previousOptions?.filter(
			({ jiraOptionId: optionId }: FieldOption) => optionId !== jiraOptionId,
		);

		if (newOptions === undefined || newOptions.length === 0) {
			// do nothing, the last option cannot be deleted
			return;
		}

		const previousValueDecorations = state.fieldValueDecorations[fieldKey] || [];
		const appliedDecoration = getAppliedDecoration(previousValueDecorations, jiraOptionId);

		const newValueDecorations = [
			...previousValueDecorations.filter(
				(decoration) => decoration.localDecorationId !== appliedDecoration?.localDecorationId,
			),
		];

		const jiraOptionIdNumber = parseInt(jiraOptionId, 10);
		const newOptionWeights = previousOptionWeights.filter(
			({ optionId }: JiraFieldOptionWeight) => optionId !== jiraOptionIdNumber,
		);
		const newConfig: FieldConfiguration = {
			...previousConfig,
			optionWeights: newOptionWeights,
		};

		setState({
			fields: {
				...state.fields,
				[fieldKey]: {
					...field,
					options: newOptions,
					configuration: newConfig,
				},
			},
			fieldValueDecorations: {
				...state.fieldValueDecorations,
				[fieldKey]: newValueDecorations,
			},
		});

		if (projectId !== undefined) {
			try {
				fieldOptionUpdateExperience.start();

				await fieldRemote.deleteFieldOption({
					fieldKey,
					optionId: jiraOptionIdNumber,
				});

				fieldOptionUpdateExperience.success();

				fieldRemote
					.updateFieldConfiguration({
						fieldKey,
						oldConfiguration: {
							...(previousOptionWeights.length > 0 ? { optionWeights: previousOptionWeights } : {}),
							...remapValueDecorations(previousValueDecorations),
						},
						configuration: {
							...(newOptionWeights.length > 0 ? { optionWeights: newOptionWeights } : {}),
							...remapValueDecorations(newValueDecorations),
						},
						type: 'deleteOptionRelatedFieldConfiguration',
					})
					.then(() => {
						fireCompoundAnalyticsEvent.FieldConfig.optionWeightChanged(createAnalyticsEvent({}), {
							issueFieldKey: fieldKey,
							issueFieldType: field.type,
							fieldValueId: jiraOptionId,
						});

						fireCompoundAnalyticsEvent.FieldConfig.valueDecorationChanged(
							createAnalyticsEvent({}),
							{
								issueFieldKey: fieldKey,
								issueFieldType: field.type,
							},
						);
					})
					.catch(onFieldUpdateFailed);

				fireCompoundAnalyticsEvent.FieldConfig.optionDeletedSuccess(createAnalyticsEvent({}), {
					issueFieldKey: fieldKey,
					issueFieldType: field.type,
					fieldValueId: jiraOptionId,
				});
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				fireCompoundAnalyticsEvent.FieldConfig.optionDeletedFailure(createAnalyticsEvent({}));

				fieldOptionUpdateExperience.failure(err);

				onFieldUpdateFailed(err);
			}
		}
	};
